/**
 * ProposalReviewCard — one resolve proposal, shown as a before/after diff with
 * Approve / Reject / Revise.
 *
 * Distinct from ``ProposalCard`` (the chat-thread bubble, Approve/Reject only):
 * the review card leads with the DiffView and offers a third action — Revise —
 * that reveals an inline guidance box and streams a fresh rewrite. Pure
 * presentation: all state + apply logic lives in ``useProposalReviewFlow``.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import type { SectionIssue, SectionProposal } from "../../api/client";
import { isTitleFieldProposal } from "../hooks/useProposalReviewFlow";
import { buildSnippet, parsePipeRow, type TableEditSnippet } from "../services/tableEdit";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import { DiffView } from "./DiffView";
import { issueParts } from "./inspector/IssueRow";
import { TableDiffView } from "./TableDiffView";

/** Build the table-diff snippet for a TABLE proposal from its per-row edits.
 *  The header is the first parseable row of the whole-table ``original_text``
 *  (null when a marker sits in the header row itself, so it shows as data). */
export function snippetForProposal(proposal: SectionProposal): TableEditSnippet | null {
  if (!proposal.is_table || !proposal.table_row_edits?.length) return null;
  let header: string[] | null = null;
  for (const line of (proposal.original_text || "").split("\n")) {
    const cells = parsePipeRow(line);
    if (cells) {
      header = cells;
      break;
    }
  }
  const includesHeaderRow = proposal.table_row_edits.some((e) => e.row_index === 0);
  const results = proposal.table_row_edits.map((e) => ({
    rowIndex: e.row_index,
    oldCells: parsePipeRow(e.original_row) ?? [],
    rawReplacement: e.proposed_row,
  }));
  return buildSnippet(results, includesHeaderRow ? null : header);
}

/** "Label: guidance" for one flag a proposal targets — the regulatory finding
 *  when there is one, else the marker's own description. */
export function flagLine(issue: SectionIssue): string {
  const { label, description } = issueParts(issue);
  const guidance = (issue.finding_issue || description || issue.marker_text || "").trim();
  return guidance ? `${label}: ${guidance}` : label;
}

/** Title-case a title-field key for display: "study_status" → "Study Status". */
export function titleFieldLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface ProposalReviewCardProps {
  proposal: SectionProposal;
  sectionNumber: string;
  sectionTitle: string;
  /** True while a decision (approve/reject) is being applied — disables actions. */
  busy?: boolean;
  /** True while a Revise re-run is streaming — keeps the card mounted, shows the
   *  live status line, and disables the actions. */
  revising?: boolean;
  /** Live status line while ``revising`` (from the rewrite's SSE events). */
  progressLabel?: string | null;
  /** ``content`` is set when the writer edited the proposed text before approving. */
  onApprove: (content?: string) => void;
  onReject: () => void;
  onRevise: (guidance: string) => void;
  /** The run's issues by id — names each flag the change fills / leaves open.
   *  Without it the card falls back to the raw marker texts. */
  issuesById?: Readonly<Record<string, SectionIssue>>;
}

const useStyles = makeStyles({
  card: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    background: surfaceColors.paper,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    flexWrap: "wrap",
  },
  sectionNumber: {
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 700,
    color: inkColors.ink2,
    fontVariantNumeric: "tabular-nums",
  },
  sectionTitle: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
    flex: "1 1 auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  revisedChip: {
    fontFamily: fonts.mono,
    fontSize: "8.5px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    padding: "0 6px",
  },
  markers: {
    fontFamily: fonts.body,
    fontSize: "10px",
    color: inkColors.ink4,
  },
  flagList: {
    margin: 0,
    paddingLeft: "14px",
    fontFamily: fonts.body,
    fontSize: "10.5px",
    lineHeight: 1.4,
    color: inkColors.ink3,
    wordBreak: "break-word",
  },
  stillOpen: {
    fontFamily: fonts.body,
    fontSize: "10.5px",
    color: statusColors.danger,
  },
  fieldChange: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  fieldOld: { color: inkColors.ink4, textDecorationLine: "line-through" },
  fieldArrow: { color: inkColors.ink4, fontSize: "11px" },
  fieldNew: { color: inkColors.ink, fontWeight: 600 },
  progress: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
    fontStyle: "italic",
  },
  reviseBox: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: "48px",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink,
    borderRadius: "4px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    padding: "6px 8px",
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  spacer: { flex: 1 },
  approveBtn: {
    border: "none",
    borderRadius: "4px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: statusColors.cool,
    color: "#063D2B",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  secondaryBtn: {
    borderRadius: "4px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${inkColors.ink4}`,
    color: inkColors.ink2,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  rejectBtn: {
    borderRadius: "4px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
});

export const ProposalReviewCard: React.FC<ProposalReviewCardProps> = ({
  proposal,
  sectionNumber,
  sectionTitle,
  busy = false,
  revising = false,
  progressLabel = null,
  onApprove,
  onReject,
  onRevise,
  issuesById,
}) => {
  const styles = useStyles();
  const isTitleField = isTitleFieldProposal(proposal);
  const fillsIssues = proposal.issue_ids
    .map((id) => issuesById?.[id])
    .filter((i): i is SectionIssue => Boolean(i));
  const unfixed = proposal.unfixed_issue_ids ?? [];
  const unfixedLines = unfixed.map((id) => {
    const issue = issuesById?.[id];
    return issue ? flagLine(issue) : null;
  });
  const [showRevise, setShowRevise] = useState(false);
  const [guidance, setGuidance] = useState("");
  // Edit mode: the writer changes the proposed text themselves, then approves it.
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const blocked = busy || revising;
  // Table proposals render a per-cell diff grid and drop Revise (which is
  // single-span only) — mirroring the chat table-edit card.
  const tableSnippet = snippetForProposal(proposal);

  const submitRevise = () => {
    const g = guidance.trim();
    if (!g) return;
    onRevise(g);
    setGuidance("");
    setShowRevise(false);
  };

  return (
    <div
      className={styles.card}
      role="region"
      aria-label={isTitleField ? "Proposed title page change" : `Proposed change in ${sectionNumber}`}
    >
      <div className={styles.header}>
        {isTitleField ? (
          <span className={styles.sectionNumber}>Title page · {titleFieldLabel(proposal.title_field_key ?? "")}</span>
        ) : (
          <>
            <span className={styles.sectionNumber}>{sectionNumber}</span>
            <span className={styles.sectionTitle}>{sectionTitle}</span>
          </>
        )}
        {proposal.revision_count > 0 ? (
          <span className={styles.revisedChip}>revised ×{proposal.revision_count}</span>
        ) : null}
      </div>
      {fillsIssues.length > 0 ? (
        <div className={styles.markers}>
          Fills:
          <ul className={styles.flagList}>
            {fillsIssues.map((issue) => (
              <li key={issue.id}>{flagLine(issue)}</li>
            ))}
          </ul>
        </div>
      ) : proposal.marker_texts.length > 0 ? (
        <div className={styles.markers}>Fills: {proposal.marker_texts.join(" · ")}</div>
      ) : null}

      {isTitleField ? (
        <div className={styles.fieldChange} data-testid="title-field-change">
          <span className={styles.fieldOld}>{proposal.original_text || "(empty)"}</span>
          <span className={styles.fieldArrow} aria-hidden="true">
            ↓
          </span>
          <span className={styles.fieldNew}>{proposal.proposed_text}</span>
        </div>
      ) : tableSnippet ? (
        <TableDiffView snippet={tableSnippet} />
      ) : (
        <DiffView before={proposal.original_text} after={proposal.proposed_text} />
      )}

      {unfixed.length > 0 ? (
        <div className={styles.stillOpen} data-testid="still-open">
          Still open after this change:
          {unfixedLines.every(Boolean) ? (
            <ul className={styles.flagList}>
              {unfixed.map((id, i) => (
                <li key={id}>{unfixedLines[i]}</li>
              ))}
            </ul>
          ) : (
            ` ${unfixed.length} flag${unfixed.length === 1 ? "" : "s"}`
          )}
        </div>
      ) : null}

      {revising && progressLabel ? <div className={styles.progress}>{progressLabel}</div> : null}

      {editing ? (
        <div className={styles.reviseBox}>
          <textarea
            className={styles.textarea}
            aria-label="Edit the proposed text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={blocked}
            rows={Math.min(14, Math.max(4, editText.split("\n").length + 1))}
            // biome-ignore lint/a11y/noAutofocus: focus the box the writer just opened
            autoFocus
          />
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryBtn} onClick={() => setEditing(false)} disabled={blocked}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.approveBtn}
              onClick={() => {
                setEditing(false);
                onApprove(editText);
              }}
              disabled={blocked || !editText.trim()}
            >
              {busy ? "Applying…" : "Approve edit"}
            </button>
          </div>
        </div>
      ) : showRevise ? (
        <div className={styles.reviseBox}>
          <textarea
            className={styles.textarea}
            placeholder="Describe how to rewrite this (the original and current draft are kept the original and its current draft in context)…"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            disabled={blocked}
            // biome-ignore lint/a11y/noAutofocus: focus the box the writer just opened
            autoFocus
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setShowRevise(false);
                setGuidance("");
              }}
              disabled={blocked}
            >
              Cancel
            </button>
            <button type="button" className={styles.approveBtn} onClick={submitRevise} disabled={blocked || !guidance.trim()}>
              Send
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.rejectBtn} onClick={onReject} disabled={blocked}>
            Reject
          </button>
          <span className={styles.spacer} />
          {tableSnippet || isTitleField ? null : (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setEditText(proposal.proposed_text);
                setEditing(true);
              }}
              disabled={blocked}
            >
              Edit text
            </button>
          )}
          {tableSnippet ? null : (
            <button type="button" className={styles.secondaryBtn} onClick={() => setShowRevise(true)} disabled={blocked}>
              Revise
            </button>
          )}
          <button type="button" className={styles.approveBtn} onClick={() => onApprove()} disabled={blocked}>
            {busy ? "Applying…" : "Approve"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProposalReviewCard;
